import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Chip } from '@/@bothrs-ui/web/components/chip/Chip'
import { Flex, FlexRow, Margin } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { capitalize, formatLocale } from '@/modules/shared/helpers/locale'
import { activityTypeTranslations } from '@/modules/timeline/types/translations'
import { StActivityCardContainer, StActivityCardContent, StDescription } from './ActivityCard.styled'
import { HealthProfileCard } from './health-profile/HealthProfileCard'
import { RemecareCard } from './remecare/RemecareCard'

import type { FC, ReactNode } from 'react'
import type { TimelineActivity } from '@/modules/timeline/components/timeline/Timeline'
import type { PartialRecord } from '@/utils/typescript'
import type { ActivityCardProps } from './ActivityCard.types'

const cardIcons: PartialRecord<ActivityCardProps['type'], ReactNode> = {
  Appointment: <FontAwesomeIcon icon={['fal', 'calendar-day']} />,
  Intake: <FontAwesomeIcon icon={['fal', 'house-medical']} />,
  Knowledge: <FontAwesomeIcon icon={['fal', 'book']} />,
  Task: <FontAwesomeIcon icon={['fal', 'clipboard-check']} />,
  Message: <FontAwesomeIcon icon={['fal', 'comment-alt']} />,
  Brochure: <FontAwesomeIcon icon={['fal', 'book']} />,
  Document: <FontAwesomeIcon icon={['fal', 'book']} />,
  Communication: <FontAwesomeIcon icon={['fal', 'comment-alt']} />,
  Invoice: <FontAwesomeIcon icon={['fal', 'file-invoice-dollar']} />,
  Content: <FontAwesomeIcon icon={['fal', 'book-open']} />,
}

const ActivityCard: FC<TimelineActivity> = (props) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const location = useLocation()

  // Template variables
  // ------------------------------------------------------------------------- /
  const {
    id,
    type,
    carepath,
    chip,
    completed,
    description,
    scheduledTime,
    showStart,
    waitTime,
    isActive = true,
    hasPassed,
    hasExpired,
    showColorBorder = true,
    monotone = false,
    meta,
    onClick,
  } = props

  // Remecare and hylyght should be displayed as tasks
  const displayType = type === 'Hylyght' || type === 'Remecare' ? 'Task' : type

  const cardIcon = useMemo(() => cardIcons[displayType as keyof typeof cardIcons], [displayType])
  const canShowTime = useMemo(() => {
    return showStart && displayType !== 'Task'
  }, [showStart, displayType])

  const isMessageOrBrochure = displayType === 'Message' || displayType === 'Brochure'
  const isBrochure = displayType === 'Brochure'

  const itemIsSelected = location.pathname.includes(id.toString())

  // Render
  // ------------------------------------------------------------------------- /

  if (type === 'Remecare') {
    return <RemecareCard {...props} />
  }
  if (type === 'Task' && meta?.profileHealthRequest === true) {
    return completed ? <HealthProfileCard {...props} type={displayType} /> : null
  }

  return (
    <StActivityCardContainer
      type={displayType}
      hasPassed={hasPassed}
      monotone={monotone}
      showColorBorder={showColorBorder}
      selected={itemIsSelected}
      completed={!isMessageOrBrochure && !!completed}
      onClick={onClick}
    >
      <div id={`card_${id}`} style={{ position: 'absolute', top: '-60px' }} />
      <FlexRow alignItems='center'>
        {cardIcon}
        <Margin ml={theme.UI.Spacing.Space2} />

        <FlexRow flexGrow={1} alignItems='center' justifyContent='space-between'>
          <H4 color={isActive ? theme.Components.Card.ActiveText : theme.Components.Card.DisabledText} noMargin>
            {i18n._(activityTypeTranslations[displayType] as string)}
          </H4>
          {scheduledTime && canShowTime ? (
            <Span type='BodyLargeBold'>
              {formatLocale(scheduledTime, 'HH:mm')}
              {waitTime ? (
                <Span type='BodyMediumRegular' color={theme.Colors.Status.Error}>
                  {waitTime}
                </Span>
              ) : null}
            </Span>
          ) : null}

          {chip && (type === 'Task' || type === 'Content' || type === 'Hylyght') ? (
            <Chip type={chip.type}>
              {chip.icon ? (
                <FontAwesomeIcon icon={chip.icon} style={{ paddingRight: theme.UI.Spacing.Space2 }} />
              ) : null}
              <Span type='BodySmallRegular'>{chip.message}</Span>
            </Chip>
          ) : null}
        </FlexRow>
      </FlexRow>

      {description ? (
        <>
          <StActivityCardContent>
            {description.map((value, index) => {
              return (
                <Flex justifyContent='space-between' alignItems='center' key={`${value}_${index}`}>
                  <StDescription
                    type={value.type}
                    color={
                      value.color ?? (isActive ? theme.Components.Card.ActiveText : theme.Components.Card.DisabledText)
                    }
                  >
                    {value.text}
                  </StDescription>

                  {index === description.length - 1 &&
                  (isMessageOrBrochure || (!completed && !hasExpired && isActive)) ? (
                    <FontAwesomeIcon icon={['fal', isBrochure ? 'external-link' : 'chevron-right']} fontSize={16} />
                  ) : null}
                </Flex>
              )
            })}
          </StActivityCardContent>
        </>
      ) : null}

      {carepath ? <Chip type='Primary'>{capitalize(carepath)}</Chip> : null}
    </StActivityCardContainer>
  )
}

export { ActivityCard }
